/**
 * @fileoverview The customer record's lifecycle (`customers.status`) — its vocabulary, which values an
 * admin may set, and who may sign in, declared once (REPAIR-LEDGER-AND-TYPED-ID-001, owner ruling RLT-Q40
 * "Only active may sign in", 2026-09-28).
 *
 * ## Why it lives here
 *
 * `customers` is one tenant table shared by POS, the storefront, CRM and the PostalMate import. Its
 * `status` column (varchar(20), default `'active'`) had no CHECK and no declared vocabulary: each writer
 * spelled its own literal, and no sign-in path read the column at all. So a customer set to anything but
 * active still signed in to the storefront and could reset a customer-portal password. The ruling
 * declares the vocabulary and a DB CHECK first, then refuses every customer sign-in and password reset
 * for a record that is not active.
 *
 * ## The states — exactly the values production code writes (measured 2026-09-29)
 *
 * - `active` — the default; every creation path (storefront registration, walk-in, POS create, seed,
 *   a PostalMate row that was not deleted at the source).
 * - `inactive` — set by an admin edit (pos-api `PUT /customers/:id`), or by the PostalMate import for a
 *   row deleted at the source.
 * - `erased` — a storefront privacy erasure (`CustomerModel.anonymizeCustomer`), always together with
 *   `deletedAt`. Never assignable by an edit.
 * - `merged` — a record folded into another by the EntityMatcher-confirmed merge (`EntityMergeEngine`), always
 *   together with `deletedAt` (#44, owner ruling "Narrow it": merge ONLY records the matcher confirms are the same
 *   person). Terminal, like `erased`; never assignable by an edit. Before it, a merged-away record kept `active` and
 *   only `deletedAt` said it was gone.
 *
 * @module @puffin-core/status-taxonomy/customerStatus
 */

'use strict';

/**
 * The customer lifecycle — exactly what `customers`' CHECK admits.
 *
 * @type {Readonly<Object<string, string>>}
 */
const CUSTOMER_STATUS = Object.freeze({
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  ERASED: 'erased',
  MERGED: 'merged',
});

/** Every value, in declaration order. */
const CUSTOMER_STATUS_VALUES = Object.freeze(Object.values(CUSTOMER_STATUS));

/** The values an admin edit may set. `erased` is reached only through a privacy erasure. */
const CUSTOMER_STATUS_ASSIGNABLE_VALUES = Object.freeze([
  CUSTOMER_STATUS.ACTIVE,
  CUSTOMER_STATUS.INACTIVE,
]);

/**
 * May a customer record in this status sign in, or reset its password? Only `active` (RLT-Q40).
 *
 * @param {string} status - The record's `status`.
 * @returns {boolean} True only for `active`.
 */
function isCustomerSignInAllowed(status) {
  return status === CUSTOMER_STATUS.ACTIVE;
}

module.exports = {
  CUSTOMER_STATUS,
  CUSTOMER_STATUS_VALUES,
  CUSTOMER_STATUS_ASSIGNABLE_VALUES,
  isCustomerSignInAllowed,
};
